import { API_ENDPOINTS } from "../store.constants";
import { BaseApi } from "./BaseApi";

const { 
  EMPLOYEES,
  ORDERS,
  RESIDENTS,
  MEALS,
  MENUS
} = API_ENDPOINTS;

export const OrdersApi = BaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getHeldOrders: builder.query({
      query: () => `/${ORDERS}/HeldOrders`,
      transformResponse: (response) => response?.data,
      providesTags: (response) => {
        return response
        ? [
          ...response.map(({ id }) => ({
            type: ORDERS,
            id,
          })),
          ORDERS
        ]
        : [ORDERS]
      }
    }),
    deleteHeldOrder: builder.mutation({
      query: (orderId) => ({
        url: `/${ORDERS}/ResidentHoldOrder/${orderId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ORDERS, RESIDENTS]
    }),
    getSubmittedOrders: builder.query({
      query: () => `/${ORDERS}/KitchenSideSetupOrders`,
      transformResponse: (response) => response?.data,
      providesTags: (response) => {
        return response
        ? [
          ...response.map(({ id }) => ({
            type: ORDERS,
            id,
          })),
          ORDERS
        ]
        : [ORDERS]
      }
    }),
    getOrdersByDateAndMeal: builder.query({
      query: ({ diningDate, mealId }) => `/${ORDERS}/OrderManagement?diningDate=${diningDate}&mealId=${mealId}`,
      transformResponse: (response) => response?.data,
      providesTags: [ORDERS] //Just refresh when any order changes
    }),
    getSubmittedOrderById: builder.query({
      query: ({orderId, residentId }) => `/${ORDERS}/SubmittedOrder/${orderId}?residentId=${residentId ?? ""}`,
      transformResponse: (response) => response?.data,
      providesTags: (response) => {
        return response
        ? [
          {type: ORDERS, id: response},
          ORDERS
        ]
        : [ORDERS];
      }
    }),
    //with the new order management view, this also gets past orders, the API still just finds by Id
    //so it has to be a valid order exposed back to the UI via one of the GET calls
    getFutureOrder: builder.query({
      query: ({ orderId, isAdmin }) => `${ORDERS}/FutureOrders/${orderId}?isAdmin=${!!isAdmin}`,
      transformResponse: (response) => response?.data,
      providesTags: (response) => {
        return response ?
        [
          {type: ORDERS, id: response.order.id}
        ]
        : [ORDERS];
      }
    }),
    deleteOrder: builder.mutation({
      query: ({orderId, isAdmin}) => ({
        url: `${ORDERS}/Order/${orderId}?isAdmin=${!!isAdmin}`,
        method: "DELETE",
      }),
      invalidatesTags: [ORDERS, RESIDENTS, MEALS, MENUS]
    }),
    reprintOrder: builder.mutation({
      query: (orderId) => ({
        url: `${ORDERS}/ReprintOrder?orderId=${orderId}`,
        method: "POST"
      }),
    }),
    printSummary: builder.mutation({
      query: (orderId) => ({
        url: `${ORDERS}/PrintSummary?orderId=${orderId}`,
        method: "POST"
      }),
    }),
    addCooksTicket: builder.mutation({
      query: (data) => ({url: `${ORDERS}/CooksTicket`, method: "POST", body: data}),
      invalidatesTags: [ORDERS]
    }),
    addResidentOrder: builder.mutation({
      query: (orderData) => ({url: `${ORDERS}/Order`, method: "POST", body: orderData}),
      invalidatesTags: [ORDERS, RESIDENTS, MEALS, MENUS]
    }),
    printTogetherOrders: builder.mutation({
      query: (orderList) => ({url: `${ORDERS}/PrintTogether`, method: "POST", body: orderList}),
      invalidatesTags: [ORDERS, RESIDENTS, MEALS, MENUS]
    }),
    addHeldOrder: builder.mutation({
      query: (orderData) => ({url: `${ORDERS}/ResidentHoldOrder`, method: "POST", body: orderData}),
      invalidatesTags: [ORDERS, RESIDENTS]
    }),
    updateServedStatus: builder.mutation({
      query: (orderId) => ({
        url: `${ORDERS}/OrderServed/${orderId}`,
        method: "PUT"
      }),
      invalidatesTags: [ORDERS]
    }),
    getFacilityGuestCharges: builder.query({
      query: (facilityId) => `${ORDERS}/GuestCharges/${facilityId}`,
      transformResponse: (response) => response?.data,
      providesTags: (response) => {
        return response
        ? [
          ...response.map(({ id }) => ({
            type: ORDERS,
            id,
          })),
          ORDERS
        ]
        : [ORDERS]
      }
    }),
    getFacilityGuestChargeById: builder.query({
      query: (id) => `${ORDERS}/GuestChargeById/${id}`,
      transformResponse: (response) => response?.data,
      providesTags: (response) => {
        return response ?
        [
          {type: ORDERS, id: response?.id}
        ]
        : [ORDERS];
      }
    }),
    getFacilityEmployees: builder.query({
      query: (facilityId) => `${ORDERS}/Employees/${facilityId}`,
      transformResponse: (response) => response?.data?.map((emp) => {
        return {
          id: emp.id,
          label: `${emp.firstName} ${emp.lastName}`
        }
      }),
      providesTags: (response) => {
        return response ? 
        [
          ...response.map(({ id }) => ({
            type: EMPLOYEES,
            id,
          })),
        ]
        : [EMPLOYEES]
      }
    }),
    addFacilityEmployee: builder.mutation({
      query: (data) => ({
        url: `${ORDERS}/Employees`,
        method: "POST",
        body: data
      }),
      invalidatesTags: [EMPLOYEES]
    })
  })
});

export const {
  useGetHeldOrdersQuery,
  useDeleteHeldOrderMutation,
  useGetOrdersByDateAndMealQuery,
  useGetSubmittedOrdersQuery,
  useGetSubmittedOrderByIdQuery,
  useGetFutureOrderQuery,
  useDeleteOrderMutation,
  useReprintOrderMutation,
  usePrintSummaryMutation,
  useAddCooksTicketMutation,
  useAddResidentOrderMutation,
  usePrintTogetherOrdersMutation,
  useAddHeldOrderMutation,
  useUpdateServedStatusMutation,
  useGetFacilityGuestChargesQuery,
  useGetFacilityGuestChargeByIdQuery,
  useGetFacilityEmployeesQuery,
  useAddFacilityEmployeeMutation
} = OrdersApi;